/// <amd-module name="Test/TestPlansHubRefresh/Pivots/ExecutePivot/Redux/PointsSaga" />
import * as React from "react";
import { Channel, channel, effects, SagaIterator } from "redux-saga";
import { ActionsOfType } from "redux/Utils/ActionHelper";
import { getAllPossibleFields } from "Test/TestPlansHubRefresh/Pivots/ColumnOptions/ExecuteColumnOptionUtils";
import * as ExecuteConstants from "Test/TestPlansHubRefresh/Pivots/ExecutePivot/ExecuteConstants";
import { ITestPointItem, RunOptions } from "Test/TestPlansHubRefresh/Pivots/ExecutePivot/Redux/PointsContracts";
import { filterTestPoints, fetchFilteredSuitePointCounts } from "Test/TestPlansHubRefresh/Pivots/ExecutePivot/Redux/PointsFilterHelper";
import {
    getTestPointItemsFromState,
    getTestPointsCount,
    getTestPointsFromTestPointItems,
    getWITColumnOptionFields,
    getWITColumnOptionFieldsArray,
    hasColumnOptionsLoadStarted,
    isAnyExecuteFilterValueSet,
    getTestCaseItemsFromState
} from "Test/TestPlansHubRefresh/Pivots/ExecutePivot/Redux/PointsSelector";
import { isCustomField } from "Test/TestPlansHubRefresh/Pivots/ColumnOptions/ColumnOptionsCommonUtils";
import * as Resources from "Test/TestPlansHubRefresh/Pivots/ExecutePivot/Resources";
import { ITestHubDataService } from "Test/TestPlansHubRefresh/Service/TestHubDataService";
import { TelemetryConstants, TestHubRefreshConstants } from "Test/TestPlansHubRefresh/TestHub/Constants";
import { SuitesActions, SuitesActionsTypes } from "Test/TestPlansHubRefresh/TestHub/Redux/Suites/SuitesActions";
import { getIdToTestSuiteMap, getTestSuiteById } from "Test/TestPlansHubRefresh/TestHub/Redux/Suites/SuitesSelector";
import { TelemetryActions } from "Test/TestPlansHubRefresh/TestHub/Redux/TelemetryActions";
import { TestHubRefreshActions, TestHubRefreshActionsTypes } from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshActions";
import {
    getExecuteColumnOptionFields,
    getIdentityDisplayString,
    getInitiallySelectedSuiteId,
    getPlanId,
    getSelectedSuiteId,
    getTestPointPageSize,
    isAdvancedLicenseEnabled,
    isShowTestsFromChildSuitesEnabled
} from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshSelector";
import { getDataProviderContent, showErrorToast, errorHandledSaga } from "Test/TestPlansHubRefresh/TestHub/Util";
import { testHubRefreshLocalContextData } from "Test/TestPlansHubRefresh/TestHub/Utils/TestPlansHubRefreshLocalContextProvider";
import { ITestRunsMenu } from "Test/TestPlansHubRefresh/TestRunsMenu/TestRunsMenu.Types";
import { ExcludeFlags, TestCase, TestPlan, TestPlansHubRefreshData, TestPoint, TestSuite } from "TestPlanCommon/Contracts/TestPlan";
import { TestArtifactWithContinuationToken } from "TestPlanCommon/Contracts/TestPlanExtended";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { ITfsPageService } from "Tfs/Platform/Page";
import { format } from "VSS/Core/Util/String";
import { ILegacyComponentProperties, LegacyComponent } from "VSS/Legacy/Legacy";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssLocationService } from "VSS/Platform/Location";
import { IPageData, IVssPageService } from "VSS/Platform/Page";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import * as Util from "VSS/Platform/Util/Cookie";
import {
    ContentViewObserverActions,
    ContentViewObserverActionTypes,
    getCurrentFilterState,
    getFilterDetails,
    isFilterActive,
    isFilterVisible
} from "VSS/Redux/Modules/ContentViewObserver";
import { IFilterDetails } from "VSS/Redux/Modules/ContentViewObserver/ContentViewObserverContracts";
import { IFilterState } from "VSSUI/Utilities/Filter";
import { WorkItemTypeCategory } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { IWorkItemTrackingRestClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking";
import { IWorkItemService } from "Wit/WorkItem/Service/Contracts";
import { PointsActions, PointsActionsTypes } from "./PointsActions";
import { announce } from "VSS/Core/Util/Accessibility";
import { TestPointCountText, ZeroTestPointText } from "Test/TestPlansHubRefresh/Pivots/ExecutePivot/Resources";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

interface IUpdateTestCasePayload {
    testCaseId: number;
    testCaseTitle: string;
    testCaseState: string;
    testCaseAssignedTo: string;
    columnOptions: { [id: string]: any };
}

export function* PointsSaga(): SagaIterator {
    yield effects.takeLatest(PointsActionsTypes.initializeExecutePivot, _initializeExecutePivot);
    yield effects.takeLatest(TestHubRefreshActionsTypes.editColumnOptionFieldsClicked, _editColumnOptionFieldsClicked);
    yield effects.debounce(100, ContentViewObserverActionTypes.FilterStateChanged, _filterPointsInState);
    yield effects.debounce(200, ContentViewObserverActionTypes.FilterStateChanged, _fetchSuitePointCounts);
    yield effects.takeLatest(PointsActionsTypes.filterPointsInState, _filterPointsInState);
    yield effects.takeLatest(PointsActionsTypes.fetchSuitePointCounts, _fetchSuitePointCounts);
    yield effects.takeLatest(ContentViewObserverActionTypes.FilterToggled, _filterToggled);
    yield effects.takeLatest(SuitesActionsTypes.changeSelectedSuite, _changeSelectedSuite);
    yield effects.takeLatest(PointsActionsTypes.openTestResult, _openTestResult);
    yield effects.takeLatest(PointsActionsTypes.loadInitialTestPoints, _loadInitialTestPoints);
    yield effects.takeLatest(PointsActionsTypes.loadTestPointsForSelectedSuite, _loadTestPointsForSelectedSuite);
    yield effects.takeLatest(SuitesActionsTypes.loadTestPointsForSelectedSuite, _loadTestPointsForSelectedSuite);
    yield effects.takeLatest(TestHubRefreshActionsTypes.showTestsFromChildSuitesClick, _loadTestPointsForSelectedSuite);
    yield effects.takeLatest(PointsActionsTypes.updateTestPointsForSelectedSuite, _updateTestPointsForSelectedSuite);
    yield effects.takeLatest(PointsActionsTypes.openApplicationTestRunnerClick, _openApplicationTestRunnerClick);
    yield effects.takeLatest(PointsActionsTypes.openApplicationTestRunnerClickToResumeTest, _openApplicationTestRunnerClickToResumeTest);
    yield effects.takeLatest(PointsActionsTypes.openAutomatedTestCasesConfirmationDialog, _openAutomatedTestCasesConfirmationDialog);
    yield effects.takeLatest(PointsActionsTypes.openApplicationTestRunner, _openApplicationTestRunner);
    yield effects.takeLatest(PointsActionsTypes.openApplicationTestRunnerToResumeTest, _openApplicationTestRunnerToResumeTest);
    yield effects.takeLatest(PointsActionsTypes.openExistingTestCaseWitForm, _openExistingTestCaseWitForm);
    yield effects.takeLatest(PointsActionsTypes.openTestPointsRunnerClient, _openTestPointsRunnerClient);
    yield effects.takeLatest(PointsActionsTypes.resumeTest, _resumeTest);
    yield effects.takeLatest(PointsActionsTypes.refreshTestPointsForWebRunner, _refreshTestPointsForWebRunner);
    yield effects.takeLatest(PointsActionsTypes.saveColumnWidths, _saveColumnWidths);
    yield effects.takeLatest(PointsActionsTypes.fetchColumnWidths, _fetchColumnWidths);
    yield effects.takeLatest(PointsActionsTypes.bulkEditTestCases, _bulkEditTestCases);
}

export function* initializeExecutePivot(action: ActionsOfType<SuitesActions, SuitesActionsTypes.changeSelectedSuite>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const pageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const pageData = (yield effects.call([pageService, pageService.getData])) as any;

    if (!pageData || !pageData.project) {
        throw new Error("Project missing");
    }

    yield effects.put(SuitesActions.updateLoadingStatus(true));

    const projectId = pageData.project.id;

    const isAdvLicense: boolean = (yield effects.select(isAdvancedLicenseEnabled)) as any;

    let initialFilterDetails: IFilterDetails = {
        projectId,
        filterState: {},
        filterVisible: false,
        filterKey: "testPlansHubFilterKey"
    };

    const currentFilterDetails: IFilterDetails = (yield effects.select(getFilterDetails)) as any;
    // get the current filter state from store and re-store the values that were set in author tab in execute tab
    if (currentFilterDetails) {
        // restore the entire filter details
        initialFilterDetails = currentFilterDetails;

        // if author text filter contained a search text, restore the same in execute
        if (currentFilterDetails.filterState && currentFilterDetails.filterState[TestHubRefreshConstants.AUTHOR_TEXT_FILTER_ITEM_KEY]) {
            const authorTextFilterValue: string = currentFilterDetails.filterState[TestHubRefreshConstants.AUTHOR_TEXT_FILTER_ITEM_KEY]!.value;
            if (authorTextFilterValue && authorTextFilterValue.trim().length > 0) {
                initialFilterDetails.filterState[TestHubRefreshConstants.EXECUTE_TEXT_FILTER_ITEM_KEY] =
                    currentFilterDetails.filterState[TestHubRefreshConstants.AUTHOR_TEXT_FILTER_ITEM_KEY];
            }
        }

        // restore the state filter values from author tab
        if (currentFilterDetails.filterState && currentFilterDetails.filterState[TestHubRefreshConstants.AUTHOR_STATE_FILTER_ITEM_KEY]) {
            initialFilterDetails.filterState[TestHubRefreshConstants.EXECUTE_STATE_FILTER_ITEM_KEY] =
                currentFilterDetails.filterState[TestHubRefreshConstants.AUTHOR_STATE_FILTER_ITEM_KEY];
        }

        // restore the assignedto filter values from author tab
        if (currentFilterDetails.filterState && currentFilterDetails.filterState[TestHubRefreshConstants.AUTHOR_ASSIGNEDTO_FILTER_ITEM_KEY]) {
            initialFilterDetails.filterState[TestHubRefreshConstants.EXECUTE_ASSIGNEDTO_FILTER_ITEM_KEY] =
                currentFilterDetails.filterState[TestHubRefreshConstants.AUTHOR_ASSIGNEDTO_FILTER_ITEM_KEY];
        }
    }

    yield effects.put(ContentViewObserverActions.initializeFilterState(initialFilterDetails));
    yield effects.put(PointsActions.filterPointsInState());
    yield effects.put(PointsActions.fetchSuitePointCounts());
}

export function* editColumnOptionFieldsClicked(
    action: ActionsOfType<TestHubRefreshActions, TestHubRefreshActionsTypes.editColumnOptionFieldsClicked>
): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const columnOptionFields = (yield effects.select(getExecuteColumnOptionFields)) as any;
    const allColumnFields = getAllPossibleFields();

    const layoutManager = (yield effects.call([pageContext, pageContext.getService], "IVssLayoutManager")) as any;

    const updateColumnFieldsChannel: Channel<{}> = (yield effects.call(channel)) as any;

    const editColumnOptionFieldsProps = {
        onSuccess: (displayColumns: string[], displayColumnNames: string[], newColumnsAdded: boolean) => {
            updateColumnField(updateColumnFieldsChannel, displayColumns, displayColumnNames, newColumnsAdded);
        },
        existingColumnFields: columnOptionFields,
        allColumnFields: allColumnFields.map((field) => ({
            referenceName: field.key,
            displayName: field.name
        }))
    };
    yield effects.call([layoutManager, layoutManager.renderCallout], (onDismiss: any) => {
        const properties: ILegacyComponentProperties = {
            modules: ["TestManagement/Scripts/Shared/ColumnOptionsPanelProxy"],
            wrappedType: "column-options-panel-proxy",
            wrappedProps: editColumnOptionFieldsProps
        };
        return React.createElement(LegacyComponent, properties);
    });

    const payload = (yield effects.take(updateColumnFieldsChannel)) as any;
    const oldColumnOptionFields = (yield effects.select(getExecuteColumnOptionFields)) as any;
    const newColumnOptionFields = payload.displayColumns;

    let customColumnFieldMap: { [columnId: string]: string} = {};
    payload.displayColumns.forEach((key: string, value: string) => {
        if (isCustomField(key)) {
            customColumnFieldMap[key] = payload.displayColumnNames[value]
        }
    });

    yield effects.put(TestHubRefreshActions.updateExecuteTabCustomColumnFieldMap(customColumnFieldMap));

    yield effects.put(TestHubRefreshActions.loadExecuteColumnOptionFieldsSucceeded(payload.displayColumns));

    if (payload.newColumnsAdded) {
        if (isFeatureFlagEnabled(pageContext, TestHubRefreshConstants.FetchTestPointColumnOptionsRecursively, false)) {
            const witColumnOptionFields = (yield effects.select(getWITColumnOptionFields)) as any;
            yield effects.call(loadTestPointColumnOptionsForSelectedSuite, action.payload.suiteId, action.payload.planId, witColumnOptionFields);
        } else {
            const testSuiteMap: Map<number, TestSuite> = (yield effects.select(getIdToTestSuiteMap)) as any;
            const selectedSuite = testSuiteMap.get(action.payload.suiteId);
            const showTestsFromChildSuitesEnabled = (yield effects.select(isShowTestsFromChildSuitesEnabled)) as any;
            /**
             * If showTestsFromChildSuites is enabled then need to make calls for all the child suites also to
             * fetch data for newly added column. If not enabled, need to make only 1 call for current suite.
             */
            let suiteIds: number[] = [];
            if (selectedSuite && showTestsFromChildSuitesEnabled) {
                suiteIds = getRecursiveChildSuiteIds(selectedSuite);
            } else {
                suiteIds.push(action.payload.suiteId);
            }
            /**
             * suiteIds contains all the suites inside the currently selected suite if Show test points child suites is enabled.
             * To update test point column options, batching parallel calls for getTestCasesForSuite api for every suite
             * The calls go as per browser limit for concurrent API calls to the same domain by a webpage
             */
            for (let i = 0; i < suiteIds.length; i = i + ExecuteConstants.SuiteTestCasesBatchSize) {
                yield effects.call(
                    fetchTestCasesForAllSuites,
                    suiteIds.slice(i, Math.min(suiteIds.length, i + ExecuteConstants.SuiteTestCasesBatchSize)),
                    action.payload.planId
                );
            }
        }
    }

    const registryEntries = {
        NewExecuteColumnOptions: payload.displayColumns && payload.displayColumns.length > 0 ? payload.displayColumns.join(",") : ""
    };
    const settingsService: ISettingsService = (yield effects.call([pageContext, pageContext.getService], "ISettingsService")) as any;
    const tfsPageService: ITfsPageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const tfsPageData = (yield effects.call([tfsPageService, tfsPageService.getData])) as any;
    yield effects.call([settingsService, settingsService.setEntries], registryEntries, SettingsUserScope.Me, "project", tfsPageData.project.id);

    const telemetryPayload = {
        planId: action.payload.planId,
        suiteId: action.payload.suiteId,
        oldColumnOptionFields: oldColumnOptionFields,
        newColumnOptionFields: newColumnOptionFields,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled),
        pivot: TestHubRefreshConstants.EXECUTE_TAB_ID
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.COLUMN_OPTIONS_EDIT, telemetryPayload));
}

function updateColumnField(updateColumnFieldsChannel: Channel<any>, displayColumns: string[], displayColumnNames: string[], newColumnsAdded: boolean) {
    updateColumnFieldsChannel.put({ displayColumns: displayColumns, displayColumnNames: displayColumnNames, newColumnsAdded: newColumnsAdded });
}

/**
 * Runs to update filtered test points wrt filter state
 */
export function* filterPointsInState(): SagaIterator {
    const planId = (yield effects.select(getPlanId)) as any;
    const isFilterActiveOrValueSet: boolean = (yield effects.call(isFilterActiveOrFilterValueSet)) as any;
    let executeColumnOptionFields: string[] | undefined = (yield effects.select(getExecuteColumnOptionFields)) as any;
    let fetchTags: boolean = false;
    let testCases: TestCase[] | undefined = undefined;
    if (executeColumnOptionFields) {
        for (const field of executeColumnOptionFields) {
            if (field === TestHubRefreshConstants.WorkItemField_Tags) {
                fetchTags = true;
                break;
            }
        }
    }
    if (fetchTags) {
        testCases = (yield effects.select(getTestCaseItemsFromState)) as any;
    }
    const allTestPoints: ITestPointItem[] = (yield effects.select(getTestPointItemsFromState, fetchTags, testCases)) as any;

    if (isFilterActiveOrValueSet) {
        testHubRefreshLocalContextData.setIsExecuteFilterActiveOrSet(true);
        const filteredTestPointIds: number[] = (yield effects.call(filterTestPoints, allTestPoints, planId)) as any;
        const announcement =
            filteredTestPointIds && filteredTestPointIds.length > 0 ? format(TestPointCountText, filteredTestPointIds.length) : ZeroTestPointText;
        announce(announcement);
        yield effects.put(PointsActions.updateFilteredTestPoints(filteredTestPointIds));
    } else {
        testHubRefreshLocalContextData.setIsExecuteFilterActiveOrSet(false);
        const announcement = allTestPoints && allTestPoints.length > 0 ? format(TestPointCountText, allTestPoints.length) : ZeroTestPointText;
        announce(announcement);
        yield effects.put(PointsActions.updateFilteredTestPoints(undefined));
    }
}

/**
 * Runs to fetch point counts for suite tree
 */
export function* fetchSuitePointCounts(): SagaIterator {
    const planId = (yield effects.select(getPlanId)) as any;
    const isFilterActiveOrValueSet: boolean = (yield effects.call(isFilterActiveOrFilterValueSet)) as any;
    if (isFilterActiveOrValueSet) {
        yield effects.call(fetchFilteredSuitePointCounts, planId);
    } else {
        yield effects.put(SuitesActions.getSuitePointCount(planId, [], [], [], [], undefined));
    }
}

/**
 * Runs whenever the filter state is toggled (enabled/disabled)
 */
export function* filterToggled(action: ActionsOfType<ContentViewObserverActions, ContentViewObserverActionTypes.FilterStateChanged>): SagaIterator {
    const filterActiveOrValueSet: boolean = (yield effects.call(isFilterActiveOrFilterValueSet)) as any;
    const telemetryPayload = {
        isFilterVisible: yield effects.select(isFilterVisible),
        isFilterActive: filterActiveOrValueSet,
        pivot: TestHubRefreshConstants.EXECUTE_TAB_ID,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.FILTER_TOGGLED, telemetryPayload));
}

/**
 * Returns true if either the filter is active or if any of the filter value is set
 * Filter active = filter is visible AND any of the filter value is set
 * We need this method because we want to evaluate filter even if it is hidden but a value in it is set
 */
export function* isFilterActiveOrFilterValueSet(): SagaIterator {
    const filterActive: boolean = (yield effects.select(isFilterActive)) as any;

    const filterState: IFilterState | undefined = (yield effects.select(getCurrentFilterState)) as any;
    const isFilterValueSet: boolean = (yield effects.call(isAnyExecuteFilterValueSet, filterState)) as any;

    return filterActive || isFilterValueSet;
}

export function* getSuiteCountThresholdFromRegistry(): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const settingsService: ISettingsService = (yield effects.call([pageContext, pageContext.getService], "ISettingsService")) as any;
    const pageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const pageData = (yield effects.call([pageService, pageService.getData])) as any;

    // fetch suite count threshold from db
    let result: { [key: string]: any } = (yield effects.call(
        [settingsService, settingsService.getEntriesAsync],
        "",
        SettingsUserScope.Me,
        "project",
        pageData.project.id
    )) as any;
    if (result && result[ExecuteConstants.SuiteCountThreshold]) {
        return result[ExecuteConstants.SuiteCountThreshold];
    } else {
        return TestHubRefreshConstants.DEFAULT_SUITE_COUNT_THRESHOLD;
    }
}

export function* changeSelectedSuite(action: ActionsOfType<SuitesActions, SuitesActionsTypes.changeSelectedSuite>): SagaIterator {
    // clear the filtered items in state, since the item ids present in it will not be valid for the new suite
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    let registryEntries = {
        SelectedSuiteId: action.payload.selectedSuiteId.toString()
    };
    const settingsService: ISettingsService = (yield effects.call([pageContext, pageContext.getService], "ISettingsService")) as any;
    const tfsPageService: ITfsPageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const tfsPageData = (yield effects.call([tfsPageService, tfsPageService.getData])) as any;
    yield effects.call([settingsService, settingsService.setEntries], registryEntries, SettingsUserScope.Me, "project", tfsPageData.project.id);

    yield effects.put(PointsActions.updateFilteredTestPoints(undefined));
    yield effects.put(PointsActions.unloadPreviousTestPoints());
    yield effects.put(PointsActions.loadTestPointsForSelectedSuite(action.payload.planId, action.payload.selectedSuiteId));
}

/**
 * opens test result page for the runId and resultId
 */
export function* openTestResult(action: ActionsOfType<PointsActions, PointsActionsTypes.openTestResult>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const tfsPageService: ITfsPageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const tfsPageData = (yield effects.call([tfsPageService, tfsPageService.getData])) as any;
    let projectName = "";
    if (tfsPageData && tfsPageData.project) {
        projectName = tfsPageData.project.name;
    }

    const locationService: IVssLocationService = (yield effects.call([pageContext, pageContext.getService], "IVssLocationService")) as any;
    const routeValues: { [key: string]: string } = {
        project: projectName
    };
    // constructing new plan result using contribution id
    let resultUrl = (yield effects.call([locationService, locationService.routeUrl], ExecuteConstants.TestResultsContributionId, routeValues)) as any;
    resultUrl += format(ExecuteConstants.queryParams, action.payload.runId, action.payload.resultId);

    const newWindow = (yield effects.call([window, window.open], resultUrl, "_blank")) as any;
    if (newWindow) {
        newWindow.opener = null;
    }

    const telemetryPayload = {
        planId: yield effects.select(getPlanId),
        suiteId: yield effects.select(getSelectedSuiteId),
        pointId: action.payload.pointId,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled),
        runId: action.payload.runId,
        resultId: action.payload.resultId
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.OPEN_TEST_RESULT, telemetryPayload));
}

/**
 * Runs to load initial test points present in json island
 */
export function* loadInitialTestPoints(action: ActionsOfType<PointsActions, PointsActionsTypes.loadInitialTestPoints>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const data: TestPlansHubRefreshData = (yield effects.call(getDataProviderContent, pageContext)) as any;

    yield effects.put(PointsActions.loadInitialTestPointsSucceeded(data.testPoints));

    if (data.testPoints) {
        yield effects.put(SuitesActions.updateLoadingStatus(false));
    }

    yield effects.put(TestHubRefreshActions.updateExecuteTabCustomColumnFieldMap(data.executeTabCustomColumnFieldMap));
    let existingColumnOptionFields = (yield effects.select(getExecuteColumnOptionFields)) as any;

    if (!existingColumnOptionFields) {
        yield effects.put(TestHubRefreshActions.loadExecuteColumnOptionFieldsSucceeded(data.executeColumnOptionFields));
    }
    yield effects.put(PointsActions.fetchColumnWidths());
}

export function* loadTestPointsForSelectedSuite(
    action:
        | ActionsOfType<PointsActions, PointsActionsTypes.loadTestPointsForSelectedSuite>
        | ActionsOfType<SuitesActions, SuitesActionsTypes.loadTestPointsForSelectedSuite>
        | ActionsOfType<TestHubRefreshActions, TestHubRefreshActionsTypes.showTestsFromChildSuitesClick>
): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;
    const data: TestPlansHubRefreshData = (yield effects.call(getDataProviderContent, pageContext)) as any;

    const showTestsFromChildSuitesEnabled = (yield effects.select(isShowTestsFromChildSuitesEnabled)) as any;
    let reload = false;
    let continuationToken = action.payload.continuationToken;
    let testPointsCount;
    if (!continuationToken) {
        reload = true;
        testPointsCount = (yield effects.select(getTestPointsCount)) as any;
        continuationToken = format("{0};{1}", TestHubRefreshConstants.INT_MIN_VALUE, TestHubRefreshConstants.INITIAL_PAGE_SIZE);
        //Changed default value of continuation token from 0 to IntMin to allow negative point ids also
    }
    // if continuation token is not present and also no test points are loaded from the data provider, then spinner should show up.
    if (reload && !testPointsCount) {
        yield effects.put(SuitesActions.updateLoadingStatus(true));
    }

    let existingColumnOptionFields = (yield effects.select(getExecuteColumnOptionFields)) as any;

    if (!existingColumnOptionFields) {
        yield effects.put(TestHubRefreshActions.updateExecuteTabCustomColumnFieldMap(data.executeTabCustomColumnFieldMap));
        yield effects.put(TestHubRefreshActions.loadExecuteColumnOptionFieldsSucceeded(data.executeColumnOptionFields));
    }

    yield effects.put(TestHubRefreshActions.updatePagingStatus(true));
    const returnIdentityRef = true;
    let testPointsWithToken: TestArtifactWithContinuationToken<TestPoint> | undefined;
    try {
        testPointsWithToken = (yield effects.call(
            [dataService, dataService.getTestPointsForSuite],
            action.payload.testPlanId,
            action.payload.testSuiteId,
            undefined,
            undefined,
            continuationToken,
            returnIdentityRef,
            true,
            showTestsFromChildSuitesEnabled
        )) as any;
    } catch (e) {
        showErrorToast(pageContext, Resources.ErrorFetchingPointsText, e.message, Resources.Refresh, () => {
            window.location.reload();
        });
    }

    if (testPointsWithToken) {
        yield effects.put(PointsActions.loadTestPointsForSelectedSuiteSucceded(testPointsWithToken.results, reload));
        //TODO: testing for paging when showChildSuites enabled or disabled.
        if (action.type === TestHubRefreshActionsTypes.showTestsFromChildSuitesClick) {
            yield effects.put(PointsActions.fetchSuitePointCounts());
        }
    }
    // we got a new payload of results here, trigger a re-evaluation of the filter in case new items match the filter
    const filterActiveOrValueSet: boolean = (yield effects.call(isFilterActiveOrFilterValueSet)) as any;
    if (filterActiveOrValueSet) {
        yield effects.put(PointsActions.filterPointsInState());
    }

    if (reload) {
        yield effects.put(SuitesActions.updateLoadingStatus(false));
    }

    // column options
    // !This ensures that the column options are called only after the reload call.
    const hasOptionsLoaded = (yield effects.select(hasColumnOptionsLoadStarted)) as any;
    let suiteIds: number[] = [];

    if (reload || !hasOptionsLoaded) {
        if (showTestsFromChildSuitesEnabled && testPointsWithToken && testPointsWithToken.results) {
            const testSuiteMap: Map<number, TestSuite> = (yield effects.select(getIdToTestSuiteMap)) as any;
            const selectedSuite = testSuiteMap.get(action.payload.testSuiteId);
            suiteIds = getRecursiveChildSuiteIds(selectedSuite);
            const suiteCountThreshold = yield effects.call(getSuiteCountThresholdFromRegistry) as any;
            if (suiteCountThreshold && suiteIds.length - 1 > suiteCountThreshold) {
                yield effects.put(
                    SuitesActions.showToastNotifications(
                        format(Resources.LargeSuiteCountToast, suiteIds.length),
                        TestHubRefreshConstants.LONG_TOAST_MESSAGE_DURATION
                    )
                );
            }
        } else {
            suiteIds.push(action.payload.testSuiteId);
        }

        if (isFeatureFlagEnabled(pageContext, TestHubRefreshConstants.FetchTestPointColumnOptionsRecursively, false)) {
            const witColumnOptionFields = (yield effects.select(getWITColumnOptionFields)) as any;
            yield effects.call(
                loadTestPointColumnOptionsForSelectedSuite,
                action.payload.testSuiteId,
                action.payload.testPlanId,
                witColumnOptionFields
            );
        } else {
            /**
             * suiteIds contains all the suites inside the currently selected suite if Show test points child suites is enabled.
             * To update test point column options, batching parallel calls for getTestCasesForSuite api for every suite
             * The calls go as per browser limit for concurrent API calls to the same domain by a webpage
             */
            for (let i = 0; i < suiteIds.length; i = i + ExecuteConstants.SuiteTestCasesBatchSize) {
                yield effects.call(
                    fetchTestCasesForAllSuites,
                    suiteIds.slice(i, Math.min(suiteIds.length, i + ExecuteConstants.SuiteTestCasesBatchSize)),
                    action.payload.testPlanId
                );
            }
        }
    }

    const pageSize: number = (yield effects.select(getTestPointPageSize)) as any;
    if (testPointsWithToken && testPointsWithToken.continuationToken) {
        const tokenSplits: string[] = testPointsWithToken.continuationToken.split(";");
        if (tokenSplits && tokenSplits.length == 2) {
            tokenSplits[1] = pageSize.toString();
            testPointsWithToken.continuationToken = tokenSplits.join(";");
        }
        yield effects.put(
            PointsActions.loadTestPointsForSelectedSuite(action.payload.testPlanId, action.payload.testSuiteId, testPointsWithToken.continuationToken)
        );
    }

    yield effects.put(TestHubRefreshActions.updatePagingStatus(false));

    if (testPointsWithToken && !testPointsWithToken.continuationToken) {
        const telemetryPayload = {
            planId: action.payload.testPlanId,
            suiteId: action.payload.testSuiteId,
            testPointsCount: yield effects.select(getTestPointsCount),
            isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled),
            testPointPageSize: pageSize
        };
        yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.GET_TEST_POINTS_FOR_SUITE, telemetryPayload));
    }
}

// Parallelizing calls to fetch test cases for each suite to improve performance
export function* fetchTestCasesForAllSuites(suiteIds: number[], testPlanId: number): any {
    const witColumnOptionFields = (yield effects.select(getWITColumnOptionFields)) as any;
    for (let i = 0; i < suiteIds.length; i++) {
        yield effects.fork(loadTestPointColumnOptionsForSelectedSuite, suiteIds[i], testPlanId, witColumnOptionFields);
    }
}

export function* loadTestPointColumnOptionsForSelectedSuite(
    testSuiteId: number,
    testPlanId: number,
    witColumnOptionFields: string,
    continuationToken?: string
): any {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;
    const showTestsFromChildSuitesEnabled = (yield effects.select(isShowTestsFromChildSuitesEnabled)) as any;
    const fetchTestPointColumnOptionsRecursively = isFeatureFlagEnabled(
        pageContext,
        TestHubRefreshConstants.FetchTestPointColumnOptionsRecursively,
        false
    );
    const isRecursive: boolean = showTestsFromChildSuitesEnabled && fetchTestPointColumnOptionsRecursively;

    let reload = false;
    if (!continuationToken && !showTestsFromChildSuitesEnabled) {
        reload = true;
    }

    let testCasesWithToken: TestArtifactWithContinuationToken<TestCase> | undefined;
    try {
        testCasesWithToken = (yield effects.call(
            [dataService, dataService.getTestCasesForSuite],
            testPlanId,
            testSuiteId,
            undefined,
            undefined,
            witColumnOptionFields,
            continuationToken,
            true,
            ExcludeFlags.None,
            true,
            isRecursive
        )) as any;
    } catch (e) {
        console.error(e);
    }

    if (testCasesWithToken) {
        yield effects.put(PointsActions.loadTestPointColumnOptionsForSelectedSuiteSucceeded(testCasesWithToken.results, reload));
    }

    // we got a new payload of results here, trigger a re-evaluation of the filter in case new items match the filter
    const filterActiveOrValueSet: boolean = (yield effects.call(isFilterActiveOrFilterValueSet)) as any;
    if (filterActiveOrValueSet) {
        yield effects.put(PointsActions.filterPointsInState());
    }

    const pageSize: number = (yield effects.select(getTestPointPageSize)) as any;
    if (testCasesWithToken && testCasesWithToken.continuationToken) {
        const tokenSplits: string[] = testCasesWithToken.continuationToken.split(";");
        if (tokenSplits && tokenSplits.length == 2) {
            tokenSplits[1] = pageSize.toString();
            testCasesWithToken.continuationToken = tokenSplits.join(";");
        }
        yield effects.call(
            loadTestPointColumnOptionsForSelectedSuite,
            testSuiteId,
            testPlanId,
            witColumnOptionFields,
            testCasesWithToken.continuationToken
        );
    }
}

// This is a Saga in called in general to update the test points. Called in the follwoing scenarios
// 1) Bulk Mark (Pass, Fail, Blocked, Not Applicable)
// 2) Reset to Active
// 3) Assign Tester
export function* updateTestPointsForSelectedSuite(
    action: ActionsOfType<PointsActions, PointsActionsTypes.updateTestPointsForSelectedSuite>
): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;

    // show spinner while test point(s) are updating
    yield effects.put(TestHubRefreshActions.updateBusyViewStatus(true));

    const testPoints: TestPoint[] = (yield effects.call(
        [dataService, dataService.updateTestPoints],
        action.payload.updateTestPoints,
        action.payload.testPlanId,
        action.payload.testSuiteId,
        true,
        true
    )) as any;

    yield effects.put(PointsActions.updateTestPointsForSelectedSuiteSucceded(testPoints));

    // if the test points are being updated in initiallySelectedSuiteId, mark the state of json island data as dirty
    const initialSelectedSuiteId = (yield effects.select(getInitiallySelectedSuiteId)) as any;
    if (initialSelectedSuiteId === action.payload.testSuiteId) {
        testHubRefreshLocalContextData.makeJsonIslandDataDirty();
    }

    // Test point(s) were updated, remove the spinner
    yield effects.put(TestHubRefreshActions.updateBusyViewStatus(false));

    // Test point(s) were updated, trigger evaluation of the filter
    const filterActiveOrValueSet: boolean = (yield effects.call(isFilterActiveOrFilterValueSet)) as any;
    if (filterActiveOrValueSet) {
        yield effects.put(PointsActions.filterPointsInState());
        yield effects.put(PointsActions.fetchSuitePointCounts());
    }
}

export function* openApplicationTestRunnerClick(
    action: ActionsOfType<PointsActions, PointsActionsTypes.openApplicationTestRunnerClick>
): SagaIterator {
    //to check whether the selected testpoints has any automated tests
    let isAnyTestPointAutomated: boolean = false;
    const testPoints: ITestPointItem[] = action.payload.points;
    for (let i = 0; i < testPoints.length; i++) {
        if (testPoints[i].isAutomated) {
            isAnyTestPointAutomated = true;
            break;
        }
    }

    let ShowDTRLaunchDialog = "false";
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    yield effects.call([pageContext, pageContext.getService], "IVssFeatureService");

    if (isFeatureFlagEnabled(pageContext, TestHubRefreshConstants.ShowDTRUpdateMsgLaunchDialog, false)) {
        const ShowHiddenPopup = (yield effects.call([Util, Util.getCookie], ExecuteConstants.HideDTRLaunchDialogCookieName))! as string;
        if (ShowHiddenPopup === "true") {
            ShowDTRLaunchDialog = "false";
        } else {
            ShowDTRLaunchDialog = ShowHiddenPopup;
        }
    } else {
        ShowDTRLaunchDialog = (yield effects.call([Util, Util.getCookie], ExecuteConstants.HideDTRLaunchDialogCookieName))! as string;
    }

    if (ShowDTRLaunchDialog === "trueAlways" || ShowDTRLaunchDialog === "true") {
        if (isAnyTestPointAutomated) {
            yield effects.put(PointsActions.openAutomatedTestCasesConfirmationDialog(action.payload));
        } else {
            yield effects.put(PointsActions.openApplicationTestRunner(action.payload));
        }
    } else {
        const layoutManager = (yield effects.call([pageContext, pageContext.getService], "IVssLayoutManager")) as any;
        const openDTRChannel: Channel<ITestRunsMenu> = (yield effects.call(channel)) as any;
        const openDTRProps = {
            onLaunch: () => {
                openDTRChannel.put(action.payload);
            }
        };

        yield effects.call([layoutManager, layoutManager.renderCallout], (onDismiss: any) => {
            const properties: ILegacyComponentProperties = {
                modules: ["TestManagement/Scripts/Shared/RunWithDTRProxy"],
                wrappedType: "run-dtr-proxy",
                wrappedProps: openDTRProps
            };
            return React.createElement(LegacyComponent, properties);
        });

        const payload = (yield effects.take(openDTRChannel)) as any;
        if (isAnyTestPointAutomated) {
            yield effects.put(PointsActions.openAutomatedTestCasesConfirmationDialog(payload));
        } else {
            yield effects.put(PointsActions.openApplicationTestRunner(payload));
        }
    }
}

/* This saga will handle click action for resume using test runner.
 * If user has chosen to skip the test runner dialog box message, then action to invoke test runner is dispatched.
 * Otherwise dialog box is rendered first and then action to invoke test runner is dispatched.
 */
export function* openApplicationTestRunnerClickToResumeTest(
    action: ActionsOfType<PointsActions, PointsActionsTypes.openApplicationTestRunnerClickToResumeTest>
): SagaIterator {
    
    if (action.payload.points && action.payload.points.length > 0) {


        let ShowDTRLaunchDialog = "false";
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        yield effects.call([pageContext, pageContext.getService], "IVssFeatureService")
        
        if (isFeatureFlagEnabled(pageContext, TestHubRefreshConstants.ShowDTRUpdateMsgLaunchDialog, false)) {
            const ShowHiddenPopup = (yield effects.call([Util, Util.getCookie], ExecuteConstants.HideDTRLaunchDialogCookieName))! as string;
            if (ShowHiddenPopup === "true") {
                ShowDTRLaunchDialog = "false";
            } else {
                ShowDTRLaunchDialog = ShowHiddenPopup;
            }
        } else {
            ShowDTRLaunchDialog = (yield effects.call([Util, Util.getCookie], ExecuteConstants.HideDTRLaunchDialogCookieName))! as string;
        }

        if (ShowDTRLaunchDialog === "trueAlways" || ShowDTRLaunchDialog === "true") {
            yield effects.put(PointsActions.openApplicationTestRunnerToResumeTest(action.payload));
        } else {
            const layoutManager = (yield effects.call([pageContext, pageContext.getService], "IVssLayoutManager")) as any;
            const openDTRChannel: Channel<ITestRunsMenu> = (yield effects.call(channel)) as any;
            const openDTRProps = {
                onLaunch: () => {
                    openDTRChannel.put(action.payload);
                }
            };

            yield effects.call([layoutManager, layoutManager.renderCallout], (onDismiss: any) => {
                const properties: ILegacyComponentProperties = {
                    modules: ["TestManagement/Scripts/Shared/RunWithDTRProxy"],
                    wrappedType: "run-dtr-proxy",
                    wrappedProps: openDTRProps
                };
                return React.createElement(LegacyComponent, properties);
            });

            const payload = (yield effects.take(openDTRChannel)) as any;

            yield effects.put(PointsActions.openApplicationTestRunnerToResumeTest(payload));
        }
    } else {
        const telemetryPayload = {
            planId: action.payload.planId,
            suiteId: action.payload.suiteId,
            isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
        };
        yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.LAUNCH_APPLICATION_TEST_RUNNER_FAILURE, telemetryPayload));
    }
}


export function* openAutomatedTestCasesConfirmationDialog(
    action: ActionsOfType<PointsActions, PointsActionsTypes.openAutomatedTestCasesConfirmationDialog>
): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const openConfirmationDialogChannel: Channel<ITestRunsMenu> = (yield effects.call(channel)) as any;
    const globalMessageService: IGlobalMessagesService = (yield effects.call([pageContext, pageContext.getService], "IGlobalMessagesService")) as any;
    yield effects.call(
        [globalMessageService, globalMessageService.addDialog],
        {
            componentType: ExecuteConstants.ConfirmationDialogType,
            contentDependencies: ["ms.vss-features.ui-layer-content"],
            contentProperties: {
                titleProps: { text: Resources.RunContainsAutomatedPointsWarning },
                footerButtonProps: [
                    {
                        text: Resources.Cancel,
                        onClick: () => {
                            globalMessageService.closeDialog();
                        }
                    },
                    {
                        text: Resources.Ok,
                        onClick: () => {
                            openConfirmationDialogChannel.put(action.payload);
                            globalMessageService.closeDialog();
                        },
                        primary: true
                    }
                ]
            }
        },
        true,
        true
    );

    const payload = (yield effects.take(openConfirmationDialogChannel)) as any;
    yield effects.put(PointsActions.openApplicationTestRunner(payload));
}

export function* openApplicationTestRunner(action: ActionsOfType<PointsActions, PointsActionsTypes.openApplicationTestRunner>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;
    yield effects.put(TestHubRefreshActions.updateBusyViewStatus(true));
    const testSettingId = (yield effects.call([dataService, dataService.createTestSettings], action.payload.planAreaPath)) as any;

    const testPoints: TestPoint[] = (yield effects.select(getTestPointsFromTestPointItems, action.payload.points)) as any;

    const testRun = (yield effects.call(
        [dataService, dataService.createTestRun],
        action.payload.planId,
        action.payload.planName,
        action.payload.planIteration,
        testPoints,
        action.payload.suiteName,
        testSettingId,
        action.payload.selectedBuild
    )) as any;

    yield effects.call([dataService, dataService.openApplicationTestRunner], testRun.id, action.payload.suiteId);
    yield effects.put(TestHubRefreshActions.updateBusyViewStatus(false));

    const pointIdToStateMapping = {} as any;
    for (var i = 0; i < action.payload.points.length; i++) {
        pointIdToStateMapping[action.payload.points[i].id] = action.payload.points[i].outcomeItem.outcome;
    }

    const telemetryPayload = {
        planId: action.payload.planId,
        suiteId: action.payload.suiteId,
        pointsCount: action.payload.points.length,
        pointIdToStateMapping: pointIdToStateMapping,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };

    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.LAUNCH_APPLICATION_TEST_RUNNER, telemetryPayload));
}

/* This saga will handle action for invoking test runner to resume a test run.
 */
export function* openApplicationTestRunnerToResumeTest(action: ActionsOfType<PointsActions, PointsActionsTypes.openApplicationTestRunnerToResumeTest>): SagaIterator {
    try {
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;

        yield effects.put(TestHubRefreshActions.updateBusyViewStatus(true));
        const testRunId = action.payload.points[0].lastRunId;
        const testRun = { id: testRunId };
        yield effects.call([dataService, dataService.openApplicationTestRunner], testRun.id, action.payload.suiteId);
        yield effects.put(TestHubRefreshActions.updateBusyViewStatus(false));

        const telemetryPayload = {
            planId: action.payload.planId,
            suiteId: action.payload.suiteId,
            pointId: action.payload.points[0].id,
            runId: testRunId,
            isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
        };

        yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.LAUNCH_APPLICATION_TEST_RUNNER_TO_RESUME_TEST, telemetryPayload));
    } catch (ex) {
        const telemetryPayload = {
            planId: action.payload.planId,
            suiteId: action.payload.suiteId,
            pointsCount: action.payload.points.length,
            isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled),
            exceptionMessage: ex.message
        };

        yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.LAUNCH_APPLICATION_TEST_RUNNER_FAILURE, telemetryPayload));
    }

}

export function* openExistingTestCaseWitForm(action: ActionsOfType<PointsActions, PointsActionsTypes.openExistingTestCaseWitForm>): SagaIterator {
    const updateTestCaseChannel: Channel<IUpdateTestCasePayload> = (yield effects.call(channel)) as any;

    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const contributionService: IVssContributionService = (yield effects.call(
        [pageContext, pageContext.getService],
        "IVssContributionService"
    )) as any;
    yield effects.call(
        [contributionService, contributionService.getContributionsAsync],
        ["ms.vss-features.ui-responsive-viewport-content", "ms.vss-work-web.common-content"]
    );

    const columnOptionFields = (yield effects.select(getWITColumnOptionFieldsArray)) as any;
    const workItemService: IWorkItemService = (yield effects.call([pageContext, pageContext.getService], "work-item-service")) as any;
    if (workItemService) {
        yield effects.call([workItemService, workItemService.openWorkItem], action.payload.testCaseId, {
            save: (workItem: any): void => {
                const newTestCaseTitle = workItem.getTitle();
                const newTestCaseState = workItem.getState();

                // this returns the assigned-to field in unique name format like 'Azure BugBash <azbash@microsoft.com>'
                let newTestCaseAssignedTo = workItem.getFieldValue(TestHubRefreshConstants.WorkItemField_AssignedTo);
                newTestCaseAssignedTo = getIdentityDisplayString(newTestCaseAssignedTo);

                const columnOptions: { [id: string]: any } = {};
                for (const columnOptionsField of columnOptionFields) {
                    columnOptions[columnOptionsField] = workItem.getFieldValue(columnOptionsField);
                }

                updateTestCaseChannel.put({
                    testCaseId: action.payload.testCaseId,
                    testCaseTitle: newTestCaseTitle,
                    testCaseState: newTestCaseState,
                    testCaseAssignedTo: newTestCaseAssignedTo,
                    columnOptions: columnOptions
                });
            }
        });
    } else {
        throw new Error("Unable to fetch workitem service instance.");
    }

    // update the test case properties in store on client side
    const payload: IUpdateTestCasePayload = (yield effects.take(updateTestCaseChannel)) as any;
    yield effects.put(
        PointsActions.updateAllTestPointsByTestCaseId(
            payload.testCaseId,
            payload.testCaseTitle,
            payload.testCaseState,
            payload.testCaseAssignedTo,
            action.payload.testPoints,
            payload.columnOptions
        )
    );

    // Test point(s) were updated, trigger evaluation of the filter
    const filterActiveOrValueSet: boolean = (yield effects.call(isFilterActiveOrFilterValueSet)) as any;
    if (filterActiveOrValueSet) {
        yield effects.put(PointsActions.filterPointsInState());
        yield effects.put(PointsActions.fetchSuitePointCounts());
    }

    const editTestCaseTelemetryPayload = {
        suiteId: action.payload.suiteId,
        planId: action.payload.planId,
        testCaseId: payload.testCaseId,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.EDIT_TEST_CASE_USING_FORM, editTestCaseTelemetryPayload));
}

// Depending on the parameters passed it will either open Web runner or MTM or run Automated Tests
export function* openTestPointsRunnerClient(action: ActionsOfType<PointsActions, PointsActionsTypes.openTestPointsRunnerClient>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const tfsPageService: ITfsPageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const tfsPageData = (yield effects.call([tfsPageService, tfsPageService.getData])) as any;
    yield effects.put(TestHubRefreshActions.updateBusyViewStatus(true));
    const witClient: IWorkItemTrackingRestClient = (yield effects.call(
        [pageContext, pageContext.getRestClient],
        "IWorkItemTrackingRestClient"
    )) as any;
    const workItemTypeCategory: WorkItemTypeCategory = (yield effects.call(
        [witClient, witClient.getWorkItemTypeCategory],
        tfsPageData.project.id,
        ExecuteConstants.WorkItemTypeCategoryForWebRunner
    )) as any;

    const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;
    const testPlan: TestPlan = (yield effects.call([dataService, dataService.getTestPlanById], action.payload.planId)) as any;
    const pageService: IVssPageService = (yield effects.call([pageContext, pageContext.getService], "IVssPageService")) as any;
    const pageData: IPageData | undefined = (yield effects.call([pageService, pageService.getData])) as any;
    const selectedSuite = (yield effects.select(getTestSuiteById, action.payload.suiteId)) as any;
    const openRefreshPointsChannel: Channel<{}> = (yield effects.call(channel)) as any;
    const openTestPointsRunnerClientProps = {
        testPoints: action.payload.points,
        selectedSuite: selectedSuite,
        plan: {
            id: action.payload.planId,
            name: action.payload.planName,
            iteration: action.payload.planIteration,
            areaPath: action.payload.planAreaPath,
            buildUri: action.payload.selectedBuild ? action.payload.selectedBuild.url : "",
            buildDefinitionId: testPlan.buildDefinition ? testPlan.buildDefinition.id : undefined,
            releaseEnvironmentDefinition: testPlan.releaseEnvironmentDefinition
        },
        bugCategoryType: workItemTypeCategory ? workItemTypeCategory.defaultWorkItemType.name : ExecuteConstants.DefaultWorkItemTypeForWebRunner,
        ownerId: pageData ? pageData.user.id : null,
        errorCallBack: (message: string) => {
            console.error(message);
        },
        successCallBack: () => {
            openRefreshPointsChannel.put(action.payload); //only for automated testpoints with web runner
        },
        selectedBuild: action.payload.selectedBuild,
        releaseDefinition: action.payload.releaseDefinition,
        environmentId: action.payload.environmentId,
        runOption: action.payload.runOptions,
        selectedDataCollectors: action.payload.dataCollectors
    };
    const layoutManager = (yield effects.call([pageContext, pageContext.getService], "IVssLayoutManager")) as any;
    yield effects.call([layoutManager, layoutManager.renderCallout], (onDismiss: any) => {
        const properties: ILegacyComponentProperties = {
            modules: ["TestManagement/Scripts/Shared/RunTestPointsProxy"],
            wrappedType: "run-test-points-proxy",
            wrappedProps: openTestPointsRunnerClientProps
        };
        return React.createElement(LegacyComponent, properties);
    });
    yield effects.put(TestHubRefreshActions.updateBusyViewStatus(false));

    const pointIdToStateMapping = {} as any;
    for (var i = 0; i < action.payload.points.length; i++) {
        pointIdToStateMapping[action.payload.points[i].id] = action.payload.points[i].outcomeItem.outcome;
    }

    const telemetryPayload = {
        suiteId: action.payload.suiteId,
        planId: action.payload.planId,
        pointsCount: action.payload.points.length,
        pointIdToStateMapping: pointIdToStateMapping,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };

    if (action.payload.runOptions == RunOptions.RunTestUsingWebRunner) {
        yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.LAUNCH_WEB_RUNNER, telemetryPayload));
    } else if (action.payload.runOptions == RunOptions.RunTestUsingRunWithOptionsWebRunner) {
        yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.LAUNCH_RUN_WITH_OPTIONS_WEB_RUNNER, telemetryPayload));
    } else if (action.payload.runOptions == RunOptions.AutomatedTestRun) {
        yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.AUTOMATED_TEST_RUN, telemetryPayload));
    } else if (action.payload.runOptions == RunOptions.RunTestUsingOldMTM) {
        yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.RUN_WITH_OLD_MTM, telemetryPayload));
    } else if (action.payload.runOptions == RunOptions.RunTestUsingNewMTM) {
        yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.RUN_WITH_NEW_MTM, telemetryPayload));
    }
    const payload = (yield effects.take(openRefreshPointsChannel)) as any;
    yield effects.put(
        PointsActions.refreshTestPointsForWebRunner(payload.planId, payload.suiteId, testHubRefreshLocalContextData.getWebRunnerPointIds())
    );
}

/**
 * Resume paused test points via web runner
 */
export function* resumeTest(action: ActionsOfType<PointsActions, PointsActionsTypes.resumeTest>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const tfsPageService: ITfsPageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const tfsPageData = (yield effects.call([tfsPageService, tfsPageService.getData])) as any;
    yield effects.put(TestHubRefreshActions.updateBusyViewStatus(true));
    const witClient: IWorkItemTrackingRestClient = (yield effects.call(
        [pageContext, pageContext.getRestClient],
        "IWorkItemTrackingRestClient"
    )) as any;
    const workItemTypeCategory: WorkItemTypeCategory = (yield effects.call(
        [witClient, witClient.getWorkItemTypeCategory],
        tfsPageData.project.id,
        ExecuteConstants.WorkItemTypeCategoryForWebRunner
    )) as any;
    // Resume Run take testPoints array as parameter even if there is 1 point
    const testPoints = [action.payload.pointToResume];
    const selectedSuite = (yield effects.select(getTestSuiteById, action.payload.suiteId)) as any;
    const resumeTestProps = {
        testPoints: testPoints,
        selectedSuite: selectedSuite,
        bugCategoryType: workItemTypeCategory ? workItemTypeCategory.defaultWorkItemType.name : ExecuteConstants.DefaultWorkItemTypeForWebRunner,
        errorCallBack: (message: string) => {
            console.error(message);
        }
    };

    const layoutManager = (yield effects.call([pageContext, pageContext.getService], "IVssLayoutManager")) as any;
    yield effects.call([layoutManager, layoutManager.renderCallout], (onDismiss: any) => {
        const properties: ILegacyComponentProperties = {
            modules: ["TestManagement/Scripts/Shared/RunTestPointsProxy"],
            wrappedType: "run-test-points-proxy",
            wrappedProps: {
                runOption: RunOptions.ResumeTestUsingWebRunner,
                ...resumeTestProps
            }
        };
        return React.createElement(LegacyComponent, properties);
    });
    yield effects.put(TestHubRefreshActions.updateBusyViewStatus(false));
    const telemetryPayload = {
        suiteId: action.payload.suiteId,
        planId: action.payload.planId,
        pointId: action.payload.pointToResume.id,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.RESUME_TEST, telemetryPayload));
}

/**
 * Refresh test points in view when web runner is save and closed
 */
export function* refreshTestPointsForWebRunner(action: ActionsOfType<PointsActions, PointsActionsTypes.refreshTestPointsForWebRunner>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;

    let testPointsWithContinuationToken: TestArtifactWithContinuationToken<TestPoint> | undefined;
    const showTestsFromChildSuitesEnabled = (yield effects.select(isShowTestsFromChildSuitesEnabled)) as any;

    try {
        testPointsWithContinuationToken = (yield effects.call(
            [dataService, dataService.getTestPointsForSuite],
            action.payload.testPlanId,
            action.payload.testSuiteId,
            action.payload.testPointIds.join(","),
            undefined,
            undefined,
            undefined,
            true,
            showTestsFromChildSuitesEnabled
        )) as any;
    } catch (e) {
        showErrorToast(pageContext, Resources.ErrorFetchingPointsText, e.message, Resources.Refresh, () => {
            window.location.reload();
        });
    }

    if (testPointsWithContinuationToken) {
        yield effects.put(PointsActions.updateTestPointsForSelectedSuiteSucceded(testPointsWithContinuationToken.results));

        // Test point(s) were updated, trigger evaluation of the filter
        const filterActiveOrValueSet: boolean = (yield effects.call(isFilterActiveOrFilterValueSet)) as any;
        if (filterActiveOrValueSet) {
            yield effects.put(PointsActions.filterPointsInState());
            yield effects.put(PointsActions.fetchSuitePointCounts());
        }
    }

    // if the test points are being updated in initiallySelectedSuiteId, mark the state of json island data as dirty
    const initialSelectedSuiteId = (yield effects.select(getInitiallySelectedSuiteId)) as any;
    if (initialSelectedSuiteId === action.payload.testSuiteId) {
        testHubRefreshLocalContextData.makeJsonIslandDataDirty();
    }
}

export function* saveColumnWidths(action: ActionsOfType<PointsActions, PointsActionsTypes.saveColumnWidths>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const settingsService: ISettingsService = (yield effects.call([pageContext, pageContext.getService], "ISettingsService")) as any;
    const tfsPageService: ITfsPageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const tfsPageData = (yield effects.call([tfsPageService, tfsPageService.getData])) as any;

    if (action.payload && action.payload.columnAndWidthMap) {
        let registryEntries;
        registryEntries = {
            ExecuteColumnWidths: action.payload.columnAndWidthMap
        };
        yield effects.call([settingsService, settingsService.setEntries], registryEntries, SettingsUserScope.Me, "project", tfsPageData.project.id);
    }
}

export function* fetchColumnWidths(action: ActionsOfType<PointsActions, PointsActionsTypes.fetchColumnWidths>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const settingsService: ISettingsService = (yield effects.call([pageContext, pageContext.getService], "ISettingsService")) as any;
    const pageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const pageData = (yield effects.call([pageService, pageService.getData])) as any;

    // fetch the column widths from db
    let result: { [key: string]: any } = (yield effects.call(
        [settingsService, settingsService.getEntriesAsync],
        "",
        SettingsUserScope.Me,
        "project",
        pageData.project.id
    )) as any;
    if (result && result["ExecuteColumnWidths"]) {
        yield effects.put(TestHubRefreshActions.changeExecuteColumnWidths(undefined, undefined, result["ExecuteColumnWidths"]));
    }
}

export function* bulkEditTestCases(action: ActionsOfType<PointsActions, PointsActionsTypes.bulkEditTestCases>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const pageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const pageData = (yield effects.call([pageService, pageService.getData])) as any;

    const witClient: IWorkItemTrackingRestClient = (yield effects.call(
        [pageContext, pageContext.getRestClient],
        "IWorkItemTrackingRestClient"
    )) as any;
    const workItemTypeCategory: WorkItemTypeCategory = (yield effects.call(
        [witClient, witClient.getWorkItemTypeCategory],
        pageData.project.id,
        ExecuteConstants.WorkItemTypeCategoryTestCase
    )) as any;
    const testCaseWorkItemTypeName = workItemTypeCategory ? workItemTypeCategory.defaultWorkItemType.name : "Test Case"

    let projectTypeMapping = new Array();
    projectTypeMapping[pageData.project.name] = [testCaseWorkItemTypeName];
    const bulkEditTestCasesProps = {
        testCaseIds: action.payload.testCaseIds,
        projectTypeMapping: projectTypeMapping,
        okCallback: (dialogResult: any) => {}
    };
    const layoutManager = (yield effects.call([pageContext, pageContext.getService], "IVssLayoutManager")) as any;
    yield effects.call([layoutManager, layoutManager.renderCallout], (onDismiss: any) => {
        const properties: ILegacyComponentProperties = {
            modules: ["TestManagement/Scripts/Shared/TestCaseBulkEditProxy"],
            wrappedType: "testcase-bulk-edit-proxy",
            wrappedProps: bulkEditTestCasesProps,
            loadingComponent: () => {
                return React.createElement(Spinner, { className: "spinner-pos", size: SpinnerSize.medium });
            }
        };

        return React.createElement(LegacyComponent, properties);
    });
}

function getRecursiveChildSuiteIds(testSuite: TestSuite | undefined): number[] {
    if (!testSuite) {
        return [];
    }

    let nextLevelChildren: TestSuite[] = testSuite.children || [];
    let totalSuiteIds: number[] = [testSuite.id];

    for (let i = 0; i < nextLevelChildren.length; i++) {
        let childSuiteIds = getRecursiveChildSuiteIds(nextLevelChildren[i]);
        totalSuiteIds = [...totalSuiteIds, ...childSuiteIds];
    }
    return totalSuiteIds;
}

const _initializeExecutePivot = errorHandledSaga(function* (
    action: ActionsOfType<SuitesActions, SuitesActionsTypes.changeSelectedSuite>
): SagaIterator {
    yield effects.call(initializeExecutePivot, action);
});

const _editColumnOptionFieldsClicked = errorHandledSaga(function* (
    action: ActionsOfType<TestHubRefreshActions, TestHubRefreshActionsTypes.editColumnOptionFieldsClicked>
): SagaIterator {
    yield effects.call(editColumnOptionFieldsClicked, action);
});

const _filterToggled = errorHandledSaga(function* (
    action: ActionsOfType<ContentViewObserverActions, ContentViewObserverActionTypes.FilterStateChanged>
): SagaIterator {
    yield effects.call(filterToggled, action);
});

const _filterPointsInState = errorHandledSaga(function* (): SagaIterator {
    yield effects.call(filterPointsInState);
});

const _fetchSuitePointCounts = errorHandledSaga(function* (): SagaIterator {
    yield effects.call(fetchSuitePointCounts);
});

const _changeSelectedSuite = errorHandledSaga(function* (action: ActionsOfType<SuitesActions, SuitesActionsTypes.changeSelectedSuite>): SagaIterator {
    yield effects.call(changeSelectedSuite, action);
});

const _openTestResult = errorHandledSaga(function* (action: ActionsOfType<PointsActions, PointsActionsTypes.openTestResult>): SagaIterator {
    yield effects.call(openTestResult, action);
});

const _loadInitialTestPoints = errorHandledSaga(function* (
    action: ActionsOfType<PointsActions, PointsActionsTypes.loadInitialTestPoints>
): SagaIterator {
    yield effects.call(loadInitialTestPoints, action);
});

const _loadTestPointsForSelectedSuite = errorHandledSaga(function* (
    action:
        | ActionsOfType<PointsActions, PointsActionsTypes.loadTestPointsForSelectedSuite>
        | ActionsOfType<SuitesActions, SuitesActionsTypes.loadTestPointsForSelectedSuite>
        | ActionsOfType<TestHubRefreshActions, TestHubRefreshActionsTypes.showTestsFromChildSuitesClick>
): SagaIterator {
    yield effects.call(loadTestPointsForSelectedSuite, action);
});

const _updateTestPointsForSelectedSuite = errorHandledSaga(function* (
    action: ActionsOfType<PointsActions, PointsActionsTypes.updateTestPointsForSelectedSuite>
): SagaIterator {
    yield effects.call(updateTestPointsForSelectedSuite, action);
});

const _openApplicationTestRunnerClick = errorHandledSaga(function* (
    action: ActionsOfType<PointsActions, PointsActionsTypes.openApplicationTestRunnerClick>
): SagaIterator {
    yield effects.call(openApplicationTestRunnerClick, action);
});

const _openApplicationTestRunnerClickToResumeTest = errorHandledSaga(function* (
    action: ActionsOfType<PointsActions, PointsActionsTypes.openApplicationTestRunnerClickToResumeTest>
): SagaIterator {
    yield effects.call(openApplicationTestRunnerClickToResumeTest, action);
});

const _openAutomatedTestCasesConfirmationDialog = errorHandledSaga(function* (
    action: ActionsOfType<PointsActions, PointsActionsTypes.openAutomatedTestCasesConfirmationDialog>
): SagaIterator {
    yield effects.call(openAutomatedTestCasesConfirmationDialog, action);
});

const _openApplicationTestRunner = errorHandledSaga(function* (
    action: ActionsOfType<PointsActions, PointsActionsTypes.openApplicationTestRunner>
): SagaIterator {
    yield effects.call(openApplicationTestRunner, action);
});

const _openApplicationTestRunnerToResumeTest = errorHandledSaga(function* (
    action: ActionsOfType<PointsActions, PointsActionsTypes.openApplicationTestRunnerToResumeTest>
): SagaIterator {
    yield effects.call(openApplicationTestRunnerToResumeTest, action);
});


const _openExistingTestCaseWitForm = errorHandledSaga(function* (
    action: ActionsOfType<PointsActions, PointsActionsTypes.openExistingTestCaseWitForm>
): SagaIterator {
    yield effects.call(openExistingTestCaseWitForm, action);
});

const _openTestPointsRunnerClient = errorHandledSaga(function* (
    action: ActionsOfType<PointsActions, PointsActionsTypes.openTestPointsRunnerClient>
): SagaIterator {
    yield effects.call(openTestPointsRunnerClient, action);
});

const _resumeTest = errorHandledSaga(function* (action: ActionsOfType<PointsActions, PointsActionsTypes.resumeTest>): SagaIterator {
    yield effects.call(resumeTest, action);
});

const _refreshTestPointsForWebRunner = errorHandledSaga(function* (
    action: ActionsOfType<PointsActions, PointsActionsTypes.refreshTestPointsForWebRunner>
): SagaIterator {
    yield effects.call(refreshTestPointsForWebRunner, action);
});

const _saveColumnWidths = errorHandledSaga(function* (action: ActionsOfType<PointsActions, PointsActionsTypes.saveColumnWidths>): SagaIterator {
    yield effects.call(saveColumnWidths, action);
});

const _fetchColumnWidths = errorHandledSaga(function* (action: ActionsOfType<PointsActions, PointsActionsTypes.fetchColumnWidths>): SagaIterator {
    yield effects.call(fetchColumnWidths, action);
});

const _bulkEditTestCases = errorHandledSaga(function* (action: ActionsOfType<PointsActions, PointsActionsTypes.bulkEditTestCases>): SagaIterator {
    yield effects.call(bulkEditTestCases, action);
});
